import {
  Button,
  ButtonBase,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material'
import numeral from 'numeral'
import { useMemo, useState } from 'react'
import {
  EmphasizedNumber,
  IconButton,
  Icons,
  ItemImageArea,
  LongPressButton,
  PointDisplay,
} from 'utoniq-ui'

import {
  Maybe,
  PersonalInformationType,
  PrepaidPointBalance,
  PrepaidPointsPaymentSourceFragment,
  SkuUnit,
  StoreItemDetailFragment,
  User,
  UserAddressFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { ContentFrame } from '@/components/layouts/ContentFrame'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { StorageImage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'
import { filterHiddenItem } from '@/lib/array'

import { Header } from './Header'

export type BuyNowItemDetailProps = {
  item?: Maybe<StoreItemDetailFragment>
  sku?: Maybe<SkuUnit>
  quantity: number
  sendingVerificationLoading: boolean
  prepaidPointBalance?: Maybe<Pick<PrepaidPointBalance, 'balance'>>
  paymentSource?: Maybe<PrepaidPointsPaymentSourceFragment>
  user: Maybe<
    Pick<
      User,
      | 'id'
      | 'fullName'
      | 'phoneNumber'
      | 'isPhoneNumberVerified'
      | 'formattedPhoneNumber'
    > &
      UserAddressFragment
  >
  formattedItemPrice: string
  onClose: () => void
  onClickClose: () => void
  onNextStep: (step: StepKey) => void
  onConfirmOrder: () => Promise<void>
  onSendVerificationSMS: () => Promise<void>
  onOpenPointPurchaseDrawer: () => void
  statements: {
    label: Maybe<string> | undefined
    value: number | undefined
  }[]
  totalPrice: number
}

export enum StepKey {
  ITEM_DETAIL = 'ITEM_DETAIL',
  USER_INFO = 'USER_INFO',
  DELIVERY_INFO = 'DELIVERY_INFO',
  PAYMENT_CREATE = 'PAYMENT_CREATE',
  COMPLETION = 'COMPLETION',
  SMS_VERIFICATION = 'SMS_VERIFICATION',
}

export const BuyNowItemDetail: Organism<BuyNowItemDetailProps> = ({
  user,
  item,
  sku,
  formattedItemPrice,
  sendingVerificationLoading,
  paymentSource,
  prepaidPointBalance,
  quantity = 1,
  onClose,
  onClickClose,
  onNextStep,
  onConfirmOrder,
  onSendVerificationSMS,
  onOpenPointPurchaseDrawer,
  totalPrice,
  statements,
}) => {
  const { t } = useTranslation('storeItemDetail')
  const [open, setOpen] = useState<boolean>(false)

  const rows = useMemo(() => {
    return [
      {
        label: t('fullName', '氏名'),
        content: (
          <Typography
            variant="body2"
            color={user?.fullName ? undefined : 'primary.main'}
          >
            {user?.fullName || t('registerName', '氏名を登録してください')}
          </Typography>
        ),
        onClick: () => onNextStep(StepKey.USER_INFO),
        right: (
          <Icons.Next
            sx={{ color: user?.fullName ? undefined : 'primary.main' }}
          />
        ),
        hidden: !item?.personalInformationRequirements?.some(
          req => req === PersonalInformationType.FullName
        ),
      },
      {
        label: t('phoneNumber', '電話番号'),
        content: (
          <>
            {!!user?.phoneNumber && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2">
                  {user?.formattedPhoneNumber}
                </Typography>
                {!user?.isPhoneNumberVerified ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icons.Error
                      fontSize="small"
                      sx={{ color: 'error.main', mt: -1 }}
                    />
                    <Typography
                      variant="body2"
                      color="error.main"
                      sx={{ width: 'max-content' }}
                    >
                      {t('unverify')}
                    </Typography>
                    {user?.phoneNumber && (
                      <Button
                        variant="text"
                        color="primary"
                        sx={{ p: 0, zIndex: 1, position: 'relative' }}
                        loading={sendingVerificationLoading}
                        onClick={async event => {
                          event.preventDefault()
                          event.stopPropagation()
                          await onSendVerificationSMS()
                        }}
                      >
                        {t('verify')}
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icons.Success
                      fontSize="small"
                      sx={{ color: 'text.hint', mt: -1 }}
                    />
                    <Typography variant="body2" color="text.hint">
                      {t('verified')}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            )}
            {!user?.phoneNumber && (
              <Typography variant="body2" color="primary.main">
                {t('registerPhoneNumber', '電話番号を登録してください')}
              </Typography>
            )}
          </>
        ),
        onClick: () => onNextStep(StepKey.USER_INFO),
        right: (
          <Icons.Next
            sx={{ color: user?.phoneNumber ? undefined : 'primary.main' }}
          />
        ),
        hidden: !item?.personalInformationRequirements?.some(
          req => req === PersonalInformationType.PhoneNumber
        ),
      },
      {
        label: item?.type ? t(`${item.type}Delivery`, 'お客様住所') : '',
        content: (
          <Typography
            variant="body2"
            color={user?.address?.fullAddress ? undefined : 'primary.main'}
          >
            {user?.address?.fullAddress ||
              t('registerAddress', '住所を登録してください')}
          </Typography>
        ),
        onClick: () => onNextStep(StepKey.DELIVERY_INFO),
        right: (
          <Icons.Next
            sx={{
              color: user?.address?.fullAddress ? undefined : 'primary.main',
            }}
          />
        ),
        hidden: !item?.personalInformationRequirements?.some(
          req => req === PersonalInformationType.Address
        ),
      },
      {
        label: t('paymentInfo'),
        content: (
          <>
            {item?.paymentType !== UserPaymentType.Cash &&
              (prepaidPointBalance?.balance ?? 0) >= totalPrice && (
                <Typography variant="body2">
                  {t('point', 'ポイント')}
                </Typography>
              )}
            {item?.paymentType !== UserPaymentType.Cash &&
              (prepaidPointBalance?.balance ?? 0) < totalPrice && (
                <Typography variant="body2" color="primary.main">
                  {t('chargePoints', 'ポイントをチャージしてください')}
                </Typography>
              )}
            {item?.paymentType === UserPaymentType.Cash &&
              !!paymentSource?.last4 && (
                <Stack alignItems="center" direction="row" spacing={2}>
                  <StorageImage
                    width={30}
                    height={24}
                    src={generateCreditCardLogoPath(paymentSource?.brand)}
                    target="common"
                  />
                  <Typography variant="body2">
                    {t('last4', { number: paymentSource?.last4 })}
                  </Typography>
                </Stack>
              )}
            {item?.paymentType === UserPaymentType.Cash &&
              !paymentSource?.last4 && (
                <Typography variant="body2" color="primary.main">
                  {t('registerPaymentInfo', '支払い情報を登録してください')}
                </Typography>
              )}
          </>
        ),
        onClick: () => {
          if (item?.paymentType !== UserPaymentType.Cash) {
            onOpenPointPurchaseDrawer()
            return
          }
          onNextStep(StepKey.PAYMENT_CREATE)
        },
        right: (
          <Icons.Next
            sx={{
              color:
                (item?.paymentType !== UserPaymentType.Cash &&
                  (prepaidPointBalance?.balance ?? 0) >= totalPrice) ||
                (item?.paymentType === UserPaymentType.Cash &&
                  !!paymentSource?.last4)
                  ? undefined
                  : 'primary.main',
            }}
          />
        ),
        hidden: !item?.price,
      },
    ]
  }, [
    t,
    user?.fullName,
    user?.phoneNumber,
    user?.formattedPhoneNumber,
    user?.isPhoneNumberVerified,
    user?.address?.fullAddress,
    item?.personalInformationRequirements,
    item?.paymentType,
    sendingVerificationLoading,
    onSendVerificationSMS,
    prepaidPointBalance?.balance,
    totalPrice,
    paymentSource?.last4,
    paymentSource?.brand,
    onNextStep,
    onOpenPointPurchaseDrawer,
  ])

  const disabledButton = useMemo<boolean>(() => {
    //check fullName
    if (
      !user?.fullName &&
      item?.personalInformationRequirements?.some(
        req => req === PersonalInformationType.FullName
      )
    ) {
      return true
    }
    //check phoneNumber
    if (
      (!user?.phoneNumber || !user?.isPhoneNumberVerified) &&
      item?.personalInformationRequirements?.some(
        req => req === PersonalInformationType.PhoneNumber
      )
    ) {
      return true
    }
    //check fullAddress
    if (
      !user?.address?.fullAddress &&
      item?.personalInformationRequirements?.some(
        req => req === PersonalInformationType.Address
      )
    ) {
      return true
    }
    if (
      item?.paymentType !== UserPaymentType.Cash &&
      (prepaidPointBalance?.balance ?? 0) < totalPrice
    ) {
      return true
    }
    if (item?.paymentType === UserPaymentType.Cash && !paymentSource?.last4) {
      return true
    }
    return false
  }, [
    item?.paymentType,
    item?.personalInformationRequirements,
    paymentSource?.last4,
    prepaidPointBalance?.balance,
    totalPrice,
    user?.address?.fullAddress,
    user?.fullName,
    user?.isPhoneNumberVerified,
    user?.phoneNumber,
  ])

  return (
    <OrganismFrame>
      <Header title={t('itemDetailTitle', '注文確認')} onClose={onClickClose} />
      <ContentFrame>
        <Stack sx={{ px: 5 }}>
          <OrderItemCell
            item={item}
            sku={sku}
            quantity={quantity}
            formattedItemPrice={formattedItemPrice}
          />
          <Divider sx={{ color: 'grey.100' }} />
          <TableContainer>
            <Table>
              <TableBody>
                {filterHiddenItem(rows).map((row, index) => (
                  <ButtonBase
                    component={TableRow}
                    key={index}
                    sx={{ display: 'table-row' }}
                    disableRipple
                    onClick={row.onClick}
                  >
                    <TableCell sx={{ px: 0, py: 3, width: 160 }}>
                      <Typography variant="subtitle1" color="text.secondary">
                        {row.label}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ pl: 2, pr: 0, py: 3 }}>
                      {row.content}
                    </TableCell>
                    <TableCell align="right" sx={{ px: 0, py: 3 }}>
                      {row.right}
                    </TableCell>
                  </ButtonBase>
                ))}
                {!!item?.price && (
                  <TableRow>
                    <TableCell
                      sx={{
                        px: 0,
                        pt: 3,
                        pb: 1,
                        width: 160,
                        border: 'none',
                      }}
                    >
                      <Typography variant="subtitle1" color="text.secondary">
                        {item?.paymentType !== UserPaymentType.Cash
                          ? t('paymentPoints', '支払いポイント')
                          : t('paymentAmount', '支払い金額')}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{ pl: 2, pr: 0, pt: 3, pb: 1, border: 'none' }}
                      colSpan={2}
                    >
                      <Stack alignItems="center" direction="row" spacing={1}>
                        <Stack direction="row" alignItems="center">
                          {item?.paymentType !== UserPaymentType.Cash ? (
                            <PointDisplay
                              size="medium"
                              point={numeral(totalPrice).format('0,0')}
                            />
                          ) : (
                            <EmphasizedNumber variant="h6">
                              ￥{numeral(totalPrice).format('0,0')}
                            </EmphasizedNumber>
                          )}
                        </Stack>
                        {open ? (
                          <IconButton.UpArrow
                            size="small"
                            onClick={() => setOpen(prevState => !prevState)}
                          />
                        ) : (
                          <IconButton.DownArrow
                            size="small"
                            onClick={() => setOpen(prevState => !prevState)}
                          />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
                {open &&
                  filterHiddenItem(statements).map((row, index) => (
                    <TableRow key={index}>
                      <TableCell
                        sx={{
                          px: 0,
                          pt: '2px',
                          pb: 1,
                          width: 160,
                          border: 'none',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {row.label}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ pl: 2, pr: 0, pt: '2px', pb: 1, border: 'none' }}
                        colSpan={2}
                      >
                        <Stack alignItems="center" direction="row">
                          <Stack direction="row" alignItems="center">
                            {item?.paymentType !== UserPaymentType.Cash ? (
                              <PointDisplay
                                size="small"
                                point={numeral(row.value).format('0,0')}
                              />
                            ) : (
                              <EmphasizedNumber variant="body2">
                                ￥{numeral(row.value).format('0,0')}
                              </EmphasizedNumber>
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            {!!item?.metadata?.ticket?.note && (
              <Typography variant="caption" color="text.secondary">
                {t('agreeNotes', '注意事項に同意して')}
              </Typography>
            )}
            <Stack spacing={4}>
              <LongPressButton
                size="large"
                onSubmit={onConfirmOrder}
                disabled={disabledButton}
                dark={config.theme?.dark}
              >
                {t('confirmOrder', '長押しで注文を確定する')}
              </LongPressButton>
              <Button variant="text" size="large" onClick={onClickClose}>
                {t('back', '戻る')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </ContentFrame>
    </OrganismFrame>
  )
}

type OrderItemCellProps = {
  item: BuyNowItemDetailProps['item']
  formattedItemPrice: BuyNowItemDetailProps['formattedItemPrice']
  sku: BuyNowItemDetailProps['sku']
  quantity: BuyNowItemDetailProps['quantity']
}
const OrderItemCell: Cell<OrderItemCellProps> = ({
  item,
  sku,
  formattedItemPrice,
  quantity,
}) => {
  const { t } = useTranslation('storeItemDetail')
  return (
    <CellFrame sx={{ py: 2 }}>
      <Stack direction="row" spacing={3}>
        <ItemImageArea
          size="small"
          src={
            sku?.thumbnail?.smallUri ??
            sku?.thumbnail?.mediumUri ??
            sku?.thumbnail?.uri ??
            item?.thumbnail.front?.mediumUri ??
            ''
          }
        />
        <Stack spacing={1} flex={1} minWidth={0}>
          <Typography
            variant="subtitle1"
            sx={{ pr: '64px', ...styleSets.lineCamp(2) }}
          >
            {item?.title}
          </Typography>
          {!!sku?.unitName && (
            <Typography variant="caption">{sku.unitName}</Typography>
          )}
          <Stack alignItems="center" direction="row" spacing={2}>
            <Stack direction="row" alignItems="center">
              {item?.paymentType !== UserPaymentType.Cash ? (
                <PointDisplay size="small" point={formattedItemPrice} />
              ) : (
                <EmphasizedNumber variant="h6">
                  ￥{formattedItemPrice}
                </EmphasizedNumber>
              )}
            </Stack>
            <Stack>
              <Typography variant="subtitle1" component="p">
                {t('quantity', { quantity })}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </CellFrame>
  )
}
